import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AnalysisPageTopbar from "../../components/metrics/AnalysisPageTopbar";
import {
  MONO_FONT,
  PAGE_FONT,
  headCellSx,
  numCellSx,
  textCellSx,
  useMetricsUi,
} from "../../components/metrics/metricsUi";
import { isMobile } from "../../globalStyles/mobile";
import { historicalInventorySnapshotHealthService } from "../../utils/services/historicalInventorySnapshotHealthService";
import {
  isManualRecaptureRunning,
  recaptureShouldRefreshHealth,
  recaptureSubmitDisabled,
  startRecapturePolling,
} from "../../utils/snapshotHealthRecaptureProgress";
import RecaptureProgressModal from "./RecaptureProgressModal";

const readIsAdmin = () => {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "null");
    if (user?.role === "admin") return true;
    const fromUser = Array.isArray(user?.roles) ? user.roles : [];
    const fromStorage = JSON.parse(localStorage.getItem("roles") || "[]");
    const roles = fromUser.length ? fromUser : fromStorage;
    return (
      Array.isArray(roles) &&
      roles.some((role) =>
        String(role?.name || role || "")
          .toLowerCase()
          .includes("admin"),
      )
    );
  } catch (_err) {
    return false;
  }
};

const formatDay = (value) => {
  if (!value) return "—";
  const raw = typeof value === "string" ? value.slice(0, 10) : value;
  const date = raw instanceof Date ? raw : new Date(`${raw}T00:00:00.000Z`);
  if (Number.isNaN(date.getTime())) return String(raw);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
};

const fmtCount = (value) => {
  if (value == null || !Number.isFinite(Number(value))) return "—";
  return Number(value).toLocaleString("en-US");
};

const fmtPct = (value) => {
  if (value == null || !Number.isFinite(Number(value))) return "N/A";
  const number = Number(value);
  return Number.isInteger(number) ? `${number}%` : `${number.toFixed(2)}%`;
};

const StatusPill = ({ status, label, ui }) => {
  const tone =
    status === "complete"
      ? { color: ui.positive, bg: ui.positiveBg }
      : status === "snapshot_in_progress"
        ? { color: ui.accent, bg: ui.accentSoft }
        : status === "snapshot_failed" || status === "no_snapshot"
          ? { color: ui.danger, bg: ui.dangerBg }
          : status === "incomplete"
            ? { color: ui.warning, bg: ui.warningBg }
            : { color: ui.colors.textSecondary, bg: ui.flatBg };

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.02em",
        color: tone.color,
        backgroundColor: tone.bg,
        borderRadius: "20px",
        px: 1.1,
        py: 0.35,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Box>
  );
};

const captureDisplay = (row, snapshotInProgress) => {
  if (snapshotInProgress) return "—";
  if (row?.status === "na" || row?.capture_pct == null) return "N/A";
  return fmtPct(row.capture_pct);
};

const HistoricalInventorySnapshotHealth = () => {
  const navigate = useNavigate();
  const ui = useMetricsUi();
  const { colors, darkMode } = ui;
  const mobile = isMobile();
  const isAdmin = useMemo(() => readIsAdmin(), []);

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState({});
  const [selected, setSelected] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [progressOpen, setProgressOpen] = useState(false);
  const [progressRun, setProgressRun] = useState(null);
  const [lastScopes, setLastScopes] = useState([]);
  const pollerRef = useRef(null);
  const healthRefreshedForRun = useRef(null);

  const fetchReport = useCallback(async (signal, options = {}) => {
    if (!options.silent) setLoading(true);
    setError("");
    try {
      const data = await historicalInventorySnapshotHealthService.fetch(signal);
      setReport(data);
    } catch (err) {
      if (err?.name === "CanceledError" || err?.name === "AbortError") return;
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to load snapshot health.",
      );
    } finally {
      if (!options.silent) setLoading(false);
    }
  }, []);

  const stopPolling = useCallback(() => {
    if (pollerRef.current) {
      pollerRef.current.stop();
      pollerRef.current = null;
    }
  }, []);

  const handleProgressUpdate = useCallback(
    (run) => {
      setProgressRun(run);
      if (recaptureShouldRefreshHealth(run, healthRefreshedForRun.current)) {
        healthRefreshedForRun.current = String(run.run_id || run._id || "");
        fetchReport(undefined, { silent: true });
      }
    },
    [fetchReport],
  );

  const startPolling = useCallback(
    (runId) => {
      if (!runId) return;
      stopPolling();
      pollerRef.current = startRecapturePolling({
        fetchRun: () =>
          historicalInventorySnapshotHealthService.fetchRun(runId),
        onUpdate: handleProgressUpdate,
      });
    },
    [handleProgressUpdate, stopPolling],
  );

  useEffect(() => () => stopPolling(), [stopPolling]);

  useEffect(() => {
    if (!isAdmin) {
      setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    fetchReport(controller.signal);
    return () => controller.abort();
  }, [fetchReport, isAdmin]);

  useEffect(() => {
    const run = report?.run;
    if (!isManualRecaptureRunning(run) || pollerRef.current) return undefined;
    setProgressRun(run);
    setProgressOpen(true);
    startPolling(run._id || run.run_id);
    return undefined;
  }, [report, startPolling]);

  const clients = report?.clients || [];
  const snapshotInProgress = Boolean(report?.snapshot_in_progress);
  const recapturing = Boolean(
    isManualRecaptureRunning(progressRun) ||
      isManualRecaptureRunning(report?.run),
  );

  const selectedScopes = useMemo(() => {
    return clients
      .map((client) => {
        const choice = selected[client.client_id];
        if (!choice) return null;
        if (choice === "all") {
          return { client_id: client.client_id, warehouse_ids: [] };
        }
        const warehouseIds = Array.from(choice);
        if (!warehouseIds.length) return null;
        return { client_id: client.client_id, warehouse_ids: warehouseIds };
      })
      .filter(Boolean);
  }, [clients, selected]);

  const selectedWarehouseCount = selectedScopes.reduce((sum, scope) => {
    if (!scope.warehouse_ids.length) {
      const client = clients.find((row) => row.client_id === scope.client_id);
      return sum + (client?.warehouses || []).length;
    }
    return sum + scope.warehouse_ids.length;
  }, 0);

  const warehouseCountForScopes = (scopes) =>
    scopes.reduce((sum, scope) => {
      if (!scope.warehouse_ids.length) {
        const client = clients.find((row) => row.client_id === scope.client_id);
        return sum + (client?.warehouses || []).length;
      }
      return sum + scope.warehouse_ids.length;
    }, 0);

  const toggleClient = (client) => {
    setSelected((current) => {
      const next = { ...current };
      if (next[client.client_id]) {
        delete next[client.client_id];
      } else {
        next[client.client_id] = "all";
      }
      return next;
    });
  };

  const toggleWarehouse = (client, warehouseId) => {
    setSelected((current) => {
      const next = { ...current };
      const warehouses = client.warehouses || [];
      const existing = next[client.client_id];
      const asSet =
        existing === "all"
          ? new Set(warehouses.map((row) => row.warehouse_id))
          : new Set(existing || []);
      if (asSet.has(warehouseId)) asSet.delete(warehouseId);
      else asSet.add(warehouseId);
      if (!asSet.size) {
        delete next[client.client_id];
      } else if (asSet.size === warehouses.length) {
        next[client.client_id] = "all";
      } else {
        next[client.client_id] = asSet;
      }
      return next;
    });
  };

  const clientChecked = (client) => Boolean(selected[client.client_id]);
  const clientIndeterminate = (client) => {
    const choice = selected[client.client_id];
    return Boolean(choice) && choice !== "all";
  };

  const handleRecapture = async (scopes = selectedScopes) => {
    if (!scopes.length) return;
    setLastScopes(scopes);
    setConfirmOpen(false);
    setProgressOpen(true);
    setProgressRun({
      status: "running",
      trigger: "manual",
      progress: {
        phase: "preparing",
        message: "Preparing recapture...",
        processed: 0,
        total: null,
        client_count: scopes.length,
        warehouse_count: warehouseCountForScopes(scopes),
      },
    });
    try {
      const started =
        await historicalInventorySnapshotHealthService.recapture(scopes);
      setProgressRun(started);
      startPolling(started.run_id || started._id);
    } catch (err) {
      stopPolling();
      setProgressRun({
        status: "failed",
        trigger: "manual",
        error:
          err?.response?.data?.message ||
          err?.message ||
          "Recapture failed. Existing snapshot rows were not deleted.",
        progress: {
          phase: "failed",
          current_client_name: null,
          current_warehouse_name: null,
        },
      });
    }
  };

  const cardSx = ui.card;

  if (!isAdmin) {
    return (
      <Box
        sx={{
          minHeight: "100%",
          backgroundColor: darkMode ? colors.bgPrimary : "#F5F6F8",
          fontFamily: PAGE_FONT,
          p: 4,
        }}
      >
        <Typography sx={{ fontWeight: 700, mb: 1 }}>
          Administrator access required
        </Typography>
        <Typography sx={{ color: colors.textSecondary, mb: 2 }}>
          Snapshot Health is limited to administrators.
        </Typography>
        <Button
          variant="outlined"
          onClick={() => navigate("/historical-inventory")}
        >
          Back to Historical Inventory
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100%",
        backgroundColor: darkMode ? colors.bgPrimary : "#F5F6F8",
        fontFamily: PAGE_FONT,
      }}
    >
      {loading && (
        <LinearProgress
          sx={{ position: "sticky", top: 0, zIndex: 10, height: 3 }}
        />
      )}
      <Box
        sx={{
          maxWidth: 1480,
          mx: "auto",
          padding: mobile ? "14px 12px 40px" : "22px 28px 56px",
        }}
      >
        <AnalysisPageTopbar
          badge="SH"
          title="Snapshot Health"
          right={
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate("/historical-inventory")}
                sx={{ textTransform: "none" }}
              >
                Historical Inventory
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => fetchReport()}
                disabled={loading}
                sx={{ textTransform: "none" }}
              >
                Refresh
              </Button>
              <Button
                size="small"
                variant="contained"
                disabled={recaptureSubmitDisabled({
                  hasSelection: selectedScopes.length > 0,
                  recapturing,
                  snapshotInProgress,
                })}
                onClick={() => setConfirmOpen(true)}
                sx={{ textTransform: "none" }}
              >
                Recapture selected
              </Button>
            </Box>
          }
        />

        <Box
          sx={{
            display: "flex",
            gap: 1.25,
            alignItems: "flex-start",
            backgroundColor: ui.surfaceAlt,
            border: `1px solid ${colors.border}`,
            borderRadius: "12px",
            px: 1.75,
            py: 1.35,
            mb: 2.5,
          }}
        >
          <InfoOutlinedIcon
            sx={{ fontSize: 17, color: colors.textTertiary, mt: "1px" }}
          />
          <Typography
            sx={{
              fontSize: 13,
              color: colors.textSecondary,
              lineHeight: 1.55,
              fontFamily: PAGE_FONT,
            }}
          >
            Capture % is distinct current SKU positions vs distinct historical
            SKU positions for the Pacific report date. Quantity 0 still counts.
            Recapture upserts the current report date only and does not delete
            existing rows or prune stale SKUs.
          </Typography>
        </Box>

        {error ? (
          <Box sx={{ ...cardSx, p: 3, mb: 2 }}>
            <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
              Unable to load report
            </Typography>
            <Typography sx={{ color: colors.textSecondary }}>{error}</Typography>
          </Box>
        ) : null}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: mobile
              ? "1fr"
              : "repeat(4, minmax(0, 1fr))",
            gap: 1.5,
            mb: 2.5,
          }}
        >
          {[
            {
              label: "Report date",
              value: formatDay(report?.report_date),
              hint: "Pacific, 2:00 AM cutover",
            },
            {
              label: "Overall capture",
              value: snapshotInProgress
                ? "In progress"
                : captureDisplay(report?.summary, false),
              hint: report?.summary?.status_label || "—",
            },
            {
              label: "Live SKU positions",
              value: fmtCount(report?.summary?.live_sku_positions),
              hint: `${fmtCount(report?.summary?.clients)} active clients`,
            },
            {
              label: "Snapshot SKU positions",
              value: fmtCount(report?.summary?.snapshot_sku_positions),
              hint: report?.run?.trigger
                ? `${report.run.trigger} · ${report.run.status}`
                : "No run recorded",
            },
          ].map((card) => (
            <Box key={card.label} sx={{ ...cardSx, p: 2 }}>
              <Typography
                sx={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: colors.textSecondary,
                  mb: 0.75,
                }}
              >
                {card.label}
              </Typography>
              <Typography
                sx={{
                  fontSize: 22,
                  fontWeight: 700,
                  fontFamily: MONO_FONT,
                  lineHeight: 1.1,
                }}
              >
                {loading && !report ? "—" : card.value}
              </Typography>
              <Typography
                sx={{ mt: 0.75, fontSize: 12, color: colors.textSecondary }}
              >
                {card.hint}
              </Typography>
            </Box>
          ))}
        </Box>

        {report?.run?.status === "failed" && report.run.error ? (
          <Box
            sx={{
              ...cardSx,
              p: 2,
              mb: 2,
              borderColor: ui.danger,
            }}
          >
            <Typography sx={{ fontWeight: 700, color: ui.danger, mb: 0.5 }}>
              Scheduled snapshot failed
            </Typography>
            <Typography sx={{ color: colors.textSecondary, fontSize: 13 }}>
              {report.run.error}
            </Typography>
          </Box>
        ) : null}

        {recapturing && !progressOpen ? (
          <Box
            sx={{
              ...cardSx,
              p: 1.75,
              mb: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Typography sx={{ fontSize: 13, color: colors.textSecondary }}>
              A manual recapture is still running. Progress is stored on the
              server.
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setProgressOpen(true)}
              sx={{ textTransform: "none", whiteSpace: "nowrap" }}
            >
              View progress
            </Button>
          </Box>
        ) : null}

        <Box sx={{ ...cardSx, overflow: "hidden" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headCellSx(ui, { width: 48 })} />
                <TableCell sx={headCellSx(ui)}>Client</TableCell>
                <TableCell sx={headCellSx(ui)} align="right">
                  Overall capture
                </TableCell>
                <TableCell sx={headCellSx(ui)} align="right">
                  Live SKUs
                </TableCell>
                <TableCell sx={headCellSx(ui)} align="right">
                  Snapshot SKUs
                </TableCell>
                <TableCell sx={headCellSx(ui)}>Status</TableCell>
                <TableCell sx={headCellSx(ui, { width: 48 })} />
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && !clients.length ? (
                <TableRow>
                  <TableCell colSpan={7} sx={textCellSx(ui)}>
                    No active clients were found.
                  </TableCell>
                </TableRow>
              ) : (
                clients.map((client) => {
                  const open = Boolean(expanded[client.client_id]);
                  return (
                    <React.Fragment key={client.client_id}>
                      <TableRow hover>
                        <TableCell sx={textCellSx(ui)}>
                          <Checkbox
                            size="small"
                            checked={clientChecked(client)}
                            indeterminate={clientIndeterminate(client)}
                            disabled={snapshotInProgress || recapturing}
                            onChange={() => toggleClient(client)}
                          />
                        </TableCell>
                        <TableCell sx={textCellSx(ui)}>
                          <Typography sx={{ fontWeight: 600, fontSize: 13 }}>
                            {client.name}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 11,
                              color: colors.textSecondary,
                            }}
                          >
                            {(client.warehouses || []).length} warehouse
                            {(client.warehouses || []).length === 1 ? "" : "s"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={numCellSx(ui)}>
                          {captureDisplay(client, snapshotInProgress)}
                        </TableCell>
                        <TableCell sx={numCellSx(ui)}>
                          {fmtCount(client.live_sku_positions)}
                        </TableCell>
                        <TableCell sx={numCellSx(ui)}>
                          {fmtCount(client.snapshot_sku_positions)}
                        </TableCell>
                        <TableCell sx={textCellSx(ui)}>
                          <StatusPill
                            status={client.status}
                            label={client.status_label}
                            ui={ui}
                          />
                        </TableCell>
                        <TableCell sx={textCellSx(ui)}>
                          <IconButton
                            size="small"
                            onClick={() =>
                              setExpanded((current) => ({
                                ...current,
                                [client.client_id]: !open,
                              }))
                            }
                            aria-label="Warehouse breakdown"
                          >
                            <ExpandMoreIcon
                              sx={{
                                transform: open ? "rotate(180deg)" : "none",
                                transition: "transform 0.15s ease",
                              }}
                            />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          colSpan={7}
                          sx={{ p: 0, borderBottom: `1px solid ${colors.border}` }}
                        >
                          <Collapse in={open} timeout="auto" unmountOnExit>
                            <Box sx={{ px: 2, py: 1.5, backgroundColor: ui.surfaceAlt }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={headCellSx(ui, { width: 48 })} />
                                    <TableCell sx={headCellSx(ui)}>
                                      Warehouse
                                    </TableCell>
                                    <TableCell sx={headCellSx(ui)} align="right">
                                      Capture
                                    </TableCell>
                                    <TableCell sx={headCellSx(ui)} align="right">
                                      Live SKUs
                                    </TableCell>
                                    <TableCell sx={headCellSx(ui)} align="right">
                                      Snapshot SKUs
                                    </TableCell>
                                    <TableCell sx={headCellSx(ui)}>
                                      Status
                                    </TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {(client.warehouses || []).map((warehouse) => {
                                    const choice = selected[client.client_id];
                                    const checked =
                                      choice === "all" ||
                                      (choice && choice.has(warehouse.warehouse_id));
                                    return (
                                      <TableRow key={warehouse.warehouse_id}>
                                        <TableCell sx={textCellSx(ui)}>
                                          <Checkbox
                                            size="small"
                                            checked={Boolean(checked)}
                                            disabled={
                                              snapshotInProgress || recapturing
                                            }
                                            onChange={() =>
                                              toggleWarehouse(
                                                client,
                                                warehouse.warehouse_id,
                                              )
                                            }
                                          />
                                        </TableCell>
                                        <TableCell sx={textCellSx(ui)}>
                                          {warehouse.name}
                                        </TableCell>
                                        <TableCell sx={numCellSx(ui)}>
                                          {captureDisplay(
                                            warehouse,
                                            snapshotInProgress,
                                          )}
                                        </TableCell>
                                        <TableCell sx={numCellSx(ui)}>
                                          {fmtCount(warehouse.live_sku_positions)}
                                        </TableCell>
                                        <TableCell sx={numCellSx(ui)}>
                                          {fmtCount(
                                            warehouse.snapshot_sku_positions,
                                          )}
                                        </TableCell>
                                        <TableCell sx={textCellSx(ui)}>
                                          <StatusPill
                                            status={warehouse.status}
                                            label={warehouse.status_label}
                                            ui={ui}
                                          />
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Box>
      </Box>

      <Dialog open={confirmOpen} onClose={() => !recapturing && setConfirmOpen(false)}>
        <DialogTitle>Recapture current report date</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14, mb: 1.5 }}>
            Recapture {formatDay(report?.report_date)} for {selectedScopes.length}{" "}
            client{selectedScopes.length === 1 ? "" : "s"} ({selectedWarehouseCount}{" "}
            warehouse{selectedWarehouseCount === 1 ? "" : "s"}).
          </Typography>
          <Typography sx={{ fontSize: 13, color: colors.textSecondary }}>
            Live inventory will be upserted onto existing historical rows for
            this date. Matching rows are updated and new current SKUs are
            inserted. Existing rows are not deleted first, and SKUs that left
            live inventory are not pruned.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmOpen(false)}
            disabled={recapturing}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => handleRecapture()}
            disabled={recapturing}
            sx={{ textTransform: "none" }}
          >
            {recapturing ? "Starting…" : "Recapture"}
          </Button>
        </DialogActions>
      </Dialog>

      <RecaptureProgressModal
        open={progressOpen}
        run={progressRun}
        reportDateLabel={formatDay(report?.report_date)}
        ui={ui}
        onHide={() => setProgressOpen(false)}
        onDone={() => {
          stopPolling();
          setProgressOpen(false);
          setProgressRun(null);
          setSelected({});
          fetchReport();
        }}
        onRetry={() => {
          stopPolling();
          handleRecapture(lastScopes.length ? lastScopes : selectedScopes);
        }}
      />
    </Box>
  );
};

export default HistoricalInventorySnapshotHealth;
